<template>
    <div>
        <br/>
        <loader v-if="loading" />
        <div v-if="!loading">
        <chart v-if="!loading" :chartData="timelineChartData"/>
        </div>
    </div>
</template>

<script>
import Chart from '../StatisticalInformation/Chart.vue';
import Loader from '@/components/Loader.vue';
import axios from 'axios';
window.Handbook = window.Handbook || {}
const apiUri = window.Handbook.ApiUri || "https://localhandbookapi.aph.gov.au/";
axios.defaults.baseURL = apiUri;

export default {
    props: ['timeline', 'loading'],
    components: {
        Loader,
        Chart
    },
    computed: {
        timelineChartData() {
            const data = this.timeline.ParliamentaryService.map(p => {
                let label = p.Electorate;
                // try to find colour based on dates
                let colour = null;
                let party = this.timeline.PartyService.find(party => p.DateStart >= party.DateStart && p.DateStart <= party.DateEnd)
                if (party) {
                    colour = party.PartyColour;
                } else {
                    colour = this.timeline.PartyService[this.timeline.PartyService.length - 1].PartyColour;
                    party = this.timeline.PartyService[this.timeline.PartyService.length - 1];
                }

                return {
                    x: new Date(p.DateStart).valueOf(),
                    x2: p.DateEnd ? new Date(p.DateEnd).valueOf() : new Date().valueOf(),
                    present: p.DateEnd === null,
                    y: 0,
                    party: party.Party,
                    color: colour,
                    label,
                    description: p.Description
                }
            });
            const partyData = this.timeline.PartyService.map(p => {
                return {
                    x: p.DateStart ? new Date(p.DateStart).valueOf() : new Date(this.timeline.ParliamentaryService[0].DateStart).valueOf(),
                    x2: p.DateEnd ? new Date(p.DateEnd).valueOf() : data[data.length - 1].x2,
                    present: p.DateEnd === null && p.DateStart !== null ? true : false,
                    y: 0,
                    color: p.PartyColour,
                    label: p.Party
                }
            });

            // join these together
            const electorateDataTemp = this.timeline.ParliamentaryService.map(p => {
                return {
                    x: new Date(p.DateStart).valueOf(),
                    x2: p.DateEnd ? new Date(p.DateEnd).valueOf() : new Date().valueOf(),
                    present: p.DateEnd === null,
                    y: 1,
                    label: p.Electorate,
                }
            });

            // join continuous parts together
            let electorateData = [];
            electorateDataTemp.forEach((d, i) => {
                // if first one, add it in
                if (i === 0) {
                    electorateData.push(d);
                }
                if (i < electorateDataTemp.length - 1) {
                    // if next entry overlaps, merge them. Move to next.
                    const nextEntry = electorateDataTemp[i + 1];
                    if (nextEntry.label === d.label && new Date(nextEntry.x) <= new Date(d.x2).addDays(1)) {
                        let data = electorateData[electorateData.length - 1];
                        data.x2 = nextEntry.x2;
                        data.present = nextEntry.present;
                    } else {
                        // if no overlap, add in next one. Move to next.
                        electorateData.push(nextEntry);
                    }
                }
            });

            const electorateColours = [
                '#5A3F70',
                '#E3C2FF',
                '#A07DBC',
                '#6C7034',
                '#B8BD7D',
            ]

            // assign colours
            const uniqueElectorates = [...new Set(electorateData.map(e => e.label))];
            const electorateColoursMatched = uniqueElectorates.map((d, i) => ({electorate: d, colour: electorateColours[i % electorateColours.length] }));

            electorateData = electorateData.map(d => ({...d, color: electorateColoursMatched.find(e => e.electorate === d.label).colour }));

            const chamberData = this.timeline.ParliamentaryService.map(p => {
                return {
                    x: new Date(p.DateStart).valueOf(),
                    x2: p.DateEnd ? new Date(p.DateEnd).valueOf() : new Date().valueOf(),
                    present: p.DateEnd === null,
                    y: 2,
                    color: p.MemberOrSenator === 'Member' ? 'rgb(70, 118, 100)' : '#ab112d',
                    label: p.MemberOrSenator === 'Member' ? 'House of Representatives' : 'Senate',
                }
            });

            // chart categories depend on service type
            const memberOrSenator = [...new Set(this.timeline.ParliamentaryService.map(p => p.MemberOrSenator))];
            let areaLabel = 'Electorate';
            if (memberOrSenator.length === 2) {
                areaLabel = 'Electorate / State';
            }
            else if (memberOrSenator[0] === 'Member') {
                areaLabel = 'Electorate';
            } 
            else if (memberOrSenator[0] === 'Senator') {
                areaLabel = 'State';
            }
            
            return {
                chart: {
                    type: 'xrange',
                    backgroundColor: '#f5f5f5',
                    height: 90
                },
                exporting: {
                    enabled: false
                },
                tooltip: {
                    formatter: function () {
                        const endDate = this.point.present ? 'present' : new Date(this.x2).toLocaleDateString();
                        return `<div>${this.point.label} - ${new Date(this.x).toLocaleDateString()} - ${endDate}</div>`
                    },
                    backgroundColor: 'rgba(255, 255, 255, 0)',
                    shadow: false,
                    borderWidth: 0,
                    borderRadius: 0,
                    useHTML: true,
                },
                title: {
                    text: ''
                },
                accessibility: {
                    point: {
                        descriptionFormatter: function (point) {
                            var ix = point.index + 1,
                                category = point.yCategory,
                                from = new Date(point.x),
                                to = new Date(point.x2);
                            return ix + '. ' + category + ', ' + from.toDateString() +
                                ' to ' + to.toDateString() + '.';
                        }
                    }
                },
                plotOptions: {
                    series: {
                        colorByPoint: true
                    }
                },
                xAxis: {
                    title: {
                        text: ''
                    },
                    type: 'datetime',
                    // minimum one week intervals
                    minTickInterval: 24 * 3600 * 1000 * 7,
                    reversed: false,
                    tickLength: 0,
                    lineColor: '#dddddd'
                },
                yAxis: {
                    title: {
                        text: ''
                    },
                    categories: ['Party', areaLabel, 'Chamber'],
                    reversed: true
                },
                legend: {
                    enabled: false
                },
                series: [{
                    name: '',
                    pointWidth: 10,
                    colorByPoint: true,
                    data: [...partyData,  ...electorateData, ...chamberData,]
                }]
            }
        },
    }
}
</script>